<template>
  <div class="d-flex align-center">
    <!-- Icon + tooltip only for urgent (red/yellow) deadlines; far-future (grey) shows the plain date. -->
    <v-tooltip v-if="showBadge" top color="black">
      <template v-slot:activator="{ on, attrs }">
        <v-icon v-bind="attrs" v-on="on" :color="urgency.color" size="18" class="mr-1">{{ urgency.icon }}</v-icon>
      </template>
      <span>{{ urgencyLabel }}</span>
    </v-tooltip>
    <div class="deadline-date" :class="dateColorClass" v-html="formattedDate"/>
  </div>
</template>

<script>
  import formatDate from '@/filters/formatDate';
  import localize from '@/filters/localize';
  import deadlineUrgency from '@/helpers/deadlineUrgency';
  import {isDeadlineActionable} from '@/enums/statuses';

  // Renders a deadline date. Urgent deadlines (red/yellow) get a colored icon whose tooltip
  // shows the "time left" (#622, variant D). Far-future (grey) deadlines are left as a plain
  // date. The tooltip carries only the "time left" — the date itself is already in the cell.
  // When a `status` is supplied, urgency is shown only while the deadline is still actionable
  // (the item awaits action); resolved items (booked, rejected, expired, …) show a plain date.
  export default {
    name: 'DeadlineCell',

    props: ['date', 'status'],

    computed: {
      urgency() {
        return deadlineUrgency(this.date);
      },
      formattedDate() {
        return formatDate(this.date);
      },
      // A resolved item's deadline never lapsed against anyone → no urgency. When no status is
      // passed we keep the plain urgency behavior (backward compatible).
      actionable() {
        return this.status == null || isDeadlineActionable(this.status);
      },
      // Only red/yellow are surfaced; grey (far future) is treated as "not urgent" → plain date.
      showBadge() {
        return this.actionable && !!this.urgency && this.urgency.tier !== 'grey';
      },
      dateColorClass() {
        return this.showBadge ? `${this.urgency.color}--text` : '';
      },
      // Human-readable "time left" — days, or hours under a day (never "0 days left", which reads as expired).
      urgencyLabel() {
        if (!this.urgency) return '';
        if (this.urgency.overdue) return localize('overdue');
        if (this.urgency.daysLeft >= 1) {
          let dayWord = this.urgency.daysLeft === 1 ? localize('day') : localize('days');
          return `${this.urgency.daysLeft} ${dayWord} ${localize('left')}`;
        }
        if (this.urgency.hoursLeft >= 1) {
          let hourWord = this.urgency.hoursLeft === 1 ? localize('hour') : localize('hours');
          return `${this.urgency.hoursLeft} ${hourWord} ${localize('left')}`;
        }
        return `< 1 ${localize('hour')} ${localize('left')}`;
      },
    },
  };
</script>

<style scoped>
  /* Keep the date on one line next to the icon so the narrow column doesn't wrap it. */
  .deadline-date {
    white-space: nowrap;
  }
</style>
